import { useAuthHydration } from '@/hooks/use-auth-hydration';
import api from '@/services/api';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

export type ContractTemplate = {
  id: string;
  name: string;
  chargeScheduleConfig: {
    billingModel: string;
    recurrenceInterval?: string;
    chargeDay?: number;
    reminderType?: string;
    customReminderDays?: number;
  };
  serviceScheduleConfig: {
    frequency: string;
    daysOfWeek?: number[];
    dayOfMonth?: number;
    startTime?: string;
    endTime?: string;
  };
  processingMethod: string;
  providerId?: string;
  isSystem: boolean;
  createdAt: string;
};

export type ContractTemplatesResponse = {
  system: ContractTemplate[];
  provider: ContractTemplate[];
};

export const contractTemplateKeys = {
  all: ['contract-templates'] as const,
};

export function useContractTemplates() {
  const isHydrated = useAuthHydration();

  return useQuery({
    queryKey: contractTemplateKeys.all,
    queryFn: async () => {
      const res = await api.get<ContractTemplatesResponse>('/provider/contract-templates');
      return res.data;
    },
    enabled: isHydrated,
  });
}

export type CreateContractTemplateDto = {
  name: string;
  chargeScheduleConfig: ContractTemplate['chargeScheduleConfig'];
  serviceScheduleConfig: ContractTemplate['serviceScheduleConfig'];
  processingMethod?: string;
};

export function useCreateContractTemplate() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (dto: CreateContractTemplateDto) => {
      const res = await api.post<ContractTemplate>('/provider/contract-templates', dto);
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: contractTemplateKeys.all });
    },
  });
}

export function useDeleteContractTemplate() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      await api.delete(`/provider/contract-templates/${id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: contractTemplateKeys.all });
    },
  });
}
